import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Building2, MapPin, Loader2, FileText, Paperclip } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMBLEM = "/ginius-emblem.png";

export default function PreventivoPublic() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [importo, setImporto] = useState("");
  const [note, setNote] = useState("");
  const [showDecline, setShowDecline] = useState(false);
  const [incaricoOk, setIncaricoOk] = useState(false);

  useEffect(() => {
    axios.get(`${API}/preventivo/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Link non valido o non più attivo."))
      .finally(() => setLoading(false));
  }, [token]);

  const invia = async () => {
    if (!importo.trim()) return;
    setBusy(true);
    try {
      const { data: res } = await axios.post(`${API}/preventivo/${token}/rispondi`, { importo, note });
      setDone(res);
    } catch (e) {
      setDone({ ok: false, message: e.response?.data?.detail || "Invio non riuscito. Riprova." });
    } finally { setBusy(false); }
  };

  const declina = async () => {
    setBusy(true);
    try {
      const { data: res } = await axios.post(`${API}/preventivo/${token}/rifiuta`, { note });
      setDone(res);
    } catch (e) {
      setDone({ ok: false, message: e.response?.data?.detail || "Operazione non riuscita." });
    } finally { setBusy(false); }
  };

  const accettaIncarico = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/preventivo/${token}/accetta-incarico`);
      setIncaricoOk(true);
    } catch (e) {
      setDone({ ok: false, message: e.response?.data?.detail || "Operazione non riuscita." });
    } finally { setBusy(false); }
  };

  const fmtDate = (s) => { if (!s) return ""; try { return new Date(s).toLocaleDateString("it-IT"); } catch { return s; } };

  return (
    <div className="min-h-screen bg-[#1A1818] flex flex-col items-center px-4 py-8">
      <div className="flex items-center gap-3 mb-6">
        <img src={EMBLEM} alt="Ginius" className="w-12 h-12 rounded-xl ring-2 ring-[#D4AF5A]/70" />
        <div className="leading-none">
          <div className="font-brand font-bold text-3xl text-brand" style={{ WebkitTextStroke: "0.5px #fff" }}>Ginius<span className="text-[#D4AF5A]">.</span></div>
          {data?.titolare?.ragione_sociale && <div className="text-xs text-[#E7C9A0]" data-testid="public-header-studio">{data.titolare.ragione_sociale}</div>}
        </div>
      </div>

      <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl p-6" data-testid="preventivo-public-card">
        {loading && <div className="flex items-center justify-center py-10 text-slate-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Caricamento…</div>}
        {!loading && error && <div className="text-center py-8 text-red-700 font-semibold" data-testid="preventivo-error">{error}</div>}

        {!loading && data && !done && (
          <>
            <div className="flex items-center gap-2 text-brand font-bold text-lg mb-1"><FileText className="w-6 h-6" /> Richiesta preventivo {data.numero}</div>
            <div className="space-y-2 text-slate-700 text-sm mb-5">
              {data.condominio_nome && <div className="flex items-start gap-1.5"><Building2 className="w-4 h-4 mt-0.5 text-slate-400" /> {data.condominio_nome}</div>}
              {data.ubicazione && <div className="flex items-start gap-1.5"><MapPin className="w-4 h-4 mt-0.5 text-slate-400" /> {data.ubicazione}</div>}
              {data.descrizione && <div className="bg-slate-50 border rounded-lg p-3 whitespace-pre-wrap">{data.descrizione}</div>}
              {data.allegati_richiesta && data.allegati_richiesta.length > 0 && (
                <div data-testid="preventivo-public-allegati">
                  <div className="font-semibold mb-1">Allegati:</div>
                  <div className="flex flex-wrap gap-2">
                    {data.allegati_richiesta.map((a) => (
                      <a key={a.id} href={`${API}/preventivo/${token}/file/${a.id}`} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 bg-white text-brand font-semibold hover:bg-slate-50">
                        <Paperclip className="w-4 h-4" /> {a.original_filename || "allegato"}
                      </a>
                    ))}
                  </div>
                </div>
              )}
              {data.data_scadenza && <div><span className="font-semibold">Scadenza:</span> {fmtDate(data.data_scadenza)}</div>}
            </div>

            {data.gia_gestito ? (
              <div className="text-center py-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 font-semibold" data-testid="preventivo-gia-gestito">
                Questa richiesta è già stata gestita (stato: {data.stato}). Non è richiesta alcuna azione.
              </div>
            ) : !showDecline ? (
              <div className="space-y-3">
                {(data.incarico_accettato || incaricoOk) ? (
                  <div data-testid="preventivo-incarico-ok" className="flex items-center gap-2 p-3 rounded-lg bg-green-50 border border-green-200 text-green-800 font-semibold text-sm">
                    <CheckCircle2 className="w-5 h-5 shrink-0" /> Hai confermato che redigerai il preventivo. Inserisci l'importo qui sotto.
                  </div>
                ) : (
                  <button data-testid="preventivo-accetta-incarico-btn" disabled={busy} onClick={accettaIncarico}
                    className="w-full h-12 rounded-xl border-2 border-green-600 text-green-700 font-bold flex items-center justify-center gap-2 disabled:opacity-60">
                    <CheckCircle2 className="w-5 h-5" /> {busy ? "Attendere…" : "Accetto di redigere il preventivo"}
                  </button>
                )}
                <label className="block text-sm font-bold text-slate-700">Importo del preventivo (€) *</label>
                <input data-testid="preventivo-importo" value={importo} onChange={(e) => setImporto(e.target.value)}
                  className="w-full h-12 border-2 border-slate-300 rounded-lg px-3" placeholder="Es. 350,00" />
                <label className="block text-sm font-bold text-slate-700">Note (opzionale)</label>
                <textarea data-testid="preventivo-note" value={note} onChange={(e) => setNote(e.target.value)}
                  className="w-full border-2 border-slate-300 rounded-lg p-3 h-20" placeholder="Dettagli, tempistiche, ecc." />
                <button data-testid="preventivo-invia-btn" disabled={busy || !importo.trim()} onClick={invia}
                  className="w-full h-14 rounded-xl bg-green-700 hover:bg-green-800 text-white font-bold text-lg flex items-center justify-center gap-2 disabled:opacity-60">
                  <CheckCircle2 className="w-6 h-6" /> {busy ? "Invio…" : "Invia preventivo"}
                </button>
                <button data-testid="preventivo-declina-open" disabled={busy} onClick={() => setShowDecline(true)}
                  className="w-full h-12 rounded-xl border-2 border-red-300 text-red-700 font-semibold flex items-center justify-center gap-2">
                  <XCircle className="w-5 h-5" /> Non sono interessato
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-slate-600 text-sm">Vuoi indicare un motivo? (facoltativo)</p>
                <textarea data-testid="preventivo-declina-note" value={note} onChange={(e) => setNote(e.target.value)}
                  className="w-full border-2 border-slate-300 rounded-lg p-3 h-24" placeholder="Motivo (opzionale)" />
                <button data-testid="preventivo-declina-conferma" disabled={busy} onClick={declina}
                  className="w-full h-12 rounded-xl bg-red-700 hover:bg-red-800 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60">
                  <XCircle className="w-5 h-5" /> {busy ? "Attendere…" : "Conferma"}
                </button>
                <button onClick={() => setShowDecline(false)} className="w-full h-10 text-slate-500">Annulla</button>
              </div>
            )}
          </>
        )}

        {done && (
          <div className="text-center py-8" data-testid="preventivo-done">
            {done.ok ? <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto mb-3" /> : <XCircle className="w-16 h-16 text-amber-500 mx-auto mb-3" />}
            <p className="text-lg font-semibold text-slate-800">{done.message}</p>
            <p className="text-sm text-slate-500 mt-2">Puoi chiudere questa pagina.</p>
          </div>
        )}
      </div>
      <p className="text-xs text-slate-500 mt-6" data-testid="public-footer-studio">{data?.titolare?.ragione_sociale || "Ginius"} · Gestione manutenzioni</p>
    </div>
  );
}
